// Settings → Roles & Permissions
// Manage roles and their permissions

import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { apiFetch } from './utils/api';

function SettingsRoles() {
  const { hasPermission } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const canCreate = hasPermission('users.create');
  const canEdit = hasPermission('users.edit');
  const canDelete = hasPermission('users.delete');

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await apiFetch('/api/roles');
      const data = await response.json();
      setRoles(data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await apiFetch('/api/roles/permissions/all');
      const data = await response.json();
      setPermissions(data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const handleDelete = async (roleId, roleName) => {
    // First confirmation
    if (!window.confirm(`⚠️ DELETE ROLE: ${roleName}?\n\nThis will permanently remove this role from the database.\n\nAre you sure?`)) return;
    
    // Second confirmation
    if (!window.confirm(`⚠️ FINAL WARNING!\n\nYou are about to PERMANENTLY DELETE:\n${roleName}\n\nThis action CANNOT be undone.\n\nDelete this role?`)) return;

    try {
      const response = await apiFetch(`/api/roles/${roleId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Role deleted successfully');
        fetchRoles();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete role');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      alert('Failed to delete role');
    }
  };

  // Group permissions by module
  const permissionsByModule = permissions.reduce((acc, perm) => {
    if (!acc[perm.module]) {
      acc[perm.module] = [];
    }
    acc[perm.module].push(perm);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow">
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Roles & Permissions</h2>
          <p className="text-sm text-blue-100 mt-1">Manage user roles and their permissions</p>
        </div>
        {canCreate && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
          >
            ➕ Create Role
          </button>
        )}
      </div>

      <div className="p-6">
      <div className="bg-blue-50 rounded-lg shadow">
        <table className="min-w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Permissions
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {roles.map((role) => (
              <tr key={role.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{role.role_name}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-600">{role.description}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {role.is_system_role ? (
                    <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                      System
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                      Custom
                    </span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-600">{role.permission_count || 0} permissions</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-3">
                    {canEdit && (
                      <button
                        onClick={() => setEditingRole(role)}
                        className="px-2 py-1 text-xs text-blue-600 hover:text-blue-900 border border-blue-600 rounded hover:bg-blue-50 whitespace-nowrap"
                        title="Edit permissions"
                      >
                        ✏️
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(role.id, role.role_name)}
                        className="px-2 py-1 text-xs text-red-600 hover:text-red-900 border border-red-600 rounded hover:bg-red-50 whitespace-nowrap"
                        title="Delete role"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>

      {showCreateModal && (
        <CreateRoleModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchRoles();
          }}
        />
      )}

      {editingRole && (
        <EditRolePermissionsModal
          role={editingRole}
          permissions={permissions}
          permissionsByModule={permissionsByModule}
          onClose={() => setEditingRole(null)}
          onSuccess={() => {
            setEditingRole(null);
            fetchRoles();
          }}
        />
      )}
    </div>
  );
}

// Create Role Modal Component
function CreateRoleModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    roleName: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await apiFetch('/api/roles', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        alert('Role created successfully');
        onSuccess();
      } else {
        setError(data.error || 'Failed to create role');
      }
    } catch (error) {
      console.error('Error creating role:', error);
      setError('Failed to create role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold mb-4">Create New Role</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role Name</label>
            <input
              type="text"
              value={formData.roleName}
              onChange={(e) => setFormData({...formData, roleName: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="e.g., Project Manager"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Describe what this role can do"
              rows="3"
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Role Permissions Modal Component
function EditRolePermissionsModal({ role, permissions, permissionsByModule, onClose, onSuccess }) {
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRolePermissions();
  }, []);

  const fetchRolePermissions = async () => {
    try {
      const response = await apiFetch(`/api/roles/${role.id}/permissions`);
      const data = await response.json();
      setSelectedPermissions(data.map(p => p.id));
    } catch (error) {
      console.error('Error fetching role permissions:', error);
    }
  };

  const handleTogglePermission = (permId) => {
    if (selectedPermissions.includes(permId)) {
      setSelectedPermissions(selectedPermissions.filter(id => id !== permId));
    } else {
      setSelectedPermissions([...selectedPermissions, permId]);
    }
  };

  const handleToggleModule = (moduleName) => {
    const modulePerms = permissionsByModule[moduleName] || [];
    const modulePermIds = modulePerms.map(p => p.id);
    const allSelected = modulePermIds.every(id => selectedPermissions.includes(id));

    if (allSelected) {
      // Deselect all
      setSelectedPermissions(selectedPermissions.filter(id => !modulePermIds.includes(id)));
    } else {
      // Select all
      setSelectedPermissions([...new Set([...selectedPermissions, ...modulePermIds])]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await apiFetch(`/api/roles/${role.id}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ permissionIds: selectedPermissions })
      });

      const data = await response.json();

      if (response.ok) {
        alert('Permissions updated successfully');
        onSuccess();
      } else {
        setError(data.error || 'Failed to update permissions');
      }
    } catch (error) {
      console.error('Error updating permissions:', error);
      setError('Failed to update permissions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-2">Edit Permissions: {role.role_name}</h2>
        <p className="text-gray-600 mb-4">{role.description}</p>

        {role.is_system_role && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
            ⚠️ This is a system role. Changes will affect all users with this role.
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 mb-6">
            {Object.entries(permissionsByModule).sort().map(([moduleName, modulePerms]) => {
              const modulePermIds = modulePerms.map(p => p.id);
              const allSelected = modulePermIds.every(id => selectedPermissions.includes(id));
              const someSelected = modulePermIds.some(id => selectedPermissions.includes(id));

              return (
                <div key={moduleName} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={input => {
                        if (input) input.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={() => handleToggleModule(moduleName)}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label className="ml-3 font-medium text-gray-900 capitalize">
                      {moduleName}
                    </label>
                  </div>
                  <div className="ml-8 grid grid-cols-2 gap-2">
                    {modulePerms.map((perm) => (
                      <label key={perm.id} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={selectedPermissions.includes(perm.id)}
                          onChange={() => handleTogglePermission(perm.id)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-gray-700">{perm.action}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Permissions'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SettingsRoles;
